import React, { useState, useEffect } from 'react';
import supabase from '../../supabase/supabase';
import { FiPlus, FiTrash2, FiTag, FiPercent, FiDollarSign, FiToggleLeft, FiToggleRight, FiEdit2, FiX, FiCheck } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const AdminPromoCodes = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    min_order: '0',
    max_uses: '',
    is_active: true,
    expires_at: '',
    description: ''
  });

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('promo_codes')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) console.error("Error fetching promo codes:", error);
    if (data) setCodes(data);
    setLoading(false);
  };

  const resetForm = () => {
    setForm({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      min_order: '0',
      max_uses: '',
      is_active: true,
      expires_at: '',
      description: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      code: form.code.toUpperCase().trim(),
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value),
      min_order: Number(form.min_order) || 0,
      max_uses: form.max_uses ? Number(form.max_uses) : null,
      is_active: form.is_active,
      expires_at: form.expires_at || null,
      description: form.description
    };

    try {
      if (editingId) {
        const { error } = await supabase.from('promo_codes').update(payload).eq('id', editingId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('promo_codes').insert([payload]);
        if (error) throw error;
      }

      fetchCodes();
      resetForm();
    } catch (err) {
      alert(err.message.includes('duplicate') ? 'This promo code already exists.' : 'Error: ' + err.message);
    }
  };

  const handleEdit = (code) => {
    setForm({
      code: code.code,
      discount_type: code.discount_type,
      discount_value: String(code.discount_value),
      min_order: String(code.min_order || 0),
      max_uses: code.max_uses ? String(code.max_uses) : '',
      is_active: code.is_active,
      expires_at: code.expires_at ? code.expires_at.slice(0, 16) : '',
      description: code.description || ''
    });
    setEditingId(code.id);
    setShowForm(true);
  };

  const handleToggleActive = async (id, currentActive) => {
    await supabase.from('promo_codes').update({ is_active: !currentActive }).eq('id', id);
    fetchCodes();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this promo code?')) {
      await supabase.from('promo_codes').delete().eq('id', id);
      fetchCodes();
    }
  };

  const isExpired = (date) => date && new Date(date) < new Date();

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiTag} className="text-brand-gold w-6 h-6" />
            <h2 className="text-xl font-serif text-gray-800">Promo Codes</h2>
          </div>
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 bg-brand-darkSage text-white px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-brand-gold transition-colors"
          >
            <FiPlus size={14} />
            New Code
          </button>
        </div>

        {/* Form */}
        {showForm && (
          <div className="mb-8 p-6 bg-gray-50/50 rounded-xl border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">{editingId ? 'Edit Promo Code' : 'Create Promo Code'}</h3>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                <FiX size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Code *</label>
                  <input
                    type="text"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm font-mono font-bold outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-all uppercase"
                    placeholder="e.g. SAVE20"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Discount Type *</label>
                  <select
                    value={form.discount_type}
                    onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-all"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (Rs.)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
                    Discount Value *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={form.discount_value}
                      onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-all"
                      placeholder="0"
                      min="0"
                      step="0.01"
                      required
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                      {form.discount_type === 'percentage' ? <FiPercent size={16} /> : <FiDollarSign size={16} />}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Min. Order (Rs.)</label>
                  <input
                    type="number"
                    value={form.min_order}
                    onChange={(e) => setForm({ ...form, min_order: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-all"
                    placeholder="0"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Max Uses (empty = unlimited)</label>
                  <input
                    type="number"
                    value={form.max_uses}
                    onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-all"
                    placeholder="Unlimited"
                    min="1"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Expires At</label>
                  <input
                    type="datetime-local"
                    value={form.expires_at}
                    onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Description (internal)</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-all"
                  placeholder="e.g. Summer sale 2025"
                />
              </div>

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="w-4 h-4 accent-brand-gold"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
              </div>

              <button
                type="submit"
                className="flex items-center gap-2 bg-brand-darkSage text-white px-6 py-3 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-brand-gold transition-colors"
              >
                <FiCheck size={14} />
                {editingId ? 'Update Code' : 'Create Code'}
              </button>
            </form>
          </div>
        )}

        {/* Codes List */}
        {loading ? (
          <p className="text-gray-400 text-sm text-center py-8">Loading promo codes...</p>
        ) : codes.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiTag} className="w-8 h-8 text-gray-300" />
            </div>
            <p className="text-gray-400 text-sm">No promo codes created yet.</p>
            <p className="text-gray-300 text-xs mt-1">Click "New Code" to create your first promo code.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {codes.map(code => {
              const expired = isExpired(code.expires_at);
              const atLimit = code.max_uses && code.uses_count >= code.max_uses;

              return (
                <div
                  key={code.id}
                  className={`flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-xl border transition-colors ${
                    !code.is_active || expired || atLimit
                      ? 'bg-gray-50/50 border-gray-100 opacity-60'
                      : 'bg-white border-gray-100 hover:border-brand-gold/30'
                  }`}
                >
                  {/* Code Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono font-bold text-sm text-brand-darkSage bg-brand-sand px-2 py-0.5 rounded">
                        {code.code}
                      </span>
                      {expired && <span className="text-[9px] font-bold uppercase tracking-widest text-red-500 bg-red-50 px-2 py-0.5 rounded">Expired</span>}
                      {atLimit && <span className="text-[9px] font-bold uppercase tracking-widest text-amber-500 bg-amber-50 px-2 py-0.5 rounded">Max Used</span>}
                      {!code.is_active && <span className="text-[9px] font-bold uppercase tracking-widest text-gray-500 bg-gray-100 px-2 py-0.5 rounded">Inactive</span>}
                    </div>
                    <p className="text-xs text-gray-500">
                      {code.discount_type === 'percentage'
                        ? `${code.discount_value}% off`
                        : `Rs. ${Number(code.discount_value).toFixed(2)} off`
                      }
                      {code.min_order > 0 && ` • Min. order Rs. ${code.min_order}`}
                      {code.description && ` • ${code.description}`}
                    </p>
                  </div>

                  {/* Usage Stats */}
                  <div className="text-center sm:text-right">
                    <p className="text-xs text-gray-400">
                      <span className="font-bold text-gray-600">{code.uses_count}</span>
                      {code.max_uses ? ` / ${code.max_uses}` : ''} uses
                    </p>
                    {code.expires_at && (
                      <p className="text-[10px] text-gray-400">
                        Expires: {new Date(code.expires_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleActive(code.id, code.is_active)}
                      className={`p-2 rounded-lg transition-colors ${code.is_active ? 'text-green-500 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'}`}
                      title={code.is_active ? 'Deactivate' : 'Activate'}
                    >
                      <SafeIcon icon={code.is_active ? FiToggleRight : FiToggleLeft} size={20} />
                    </button>
                    <button
                      onClick={() => handleEdit(code)}
                      className="p-2 rounded-lg text-gray-400 hover:text-brand-gold hover:bg-brand-gold/5 transition-colors"
                      title="Edit"
                    >
                      <FiEdit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(code.id)}
                      className="p-2 rounded-lg text-gray-300 hover:text-red-500 transition-colors"
                      title="Delete"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPromoCodes;